import { QueryClient, queryOptions } from '@tanstack/react-query';
import { redirect } from '@tanstack/react-router';

import { fetchBackend } from './apiClient';

export type Project = {
  id: string;
  name: string;
};

export type ProjectMember = {
  id: string;
  email: string;
  role: string;
};

export type ProjectDetails = Project & {
  members: ProjectMember[];
  prAiAnalysisEnabled: boolean;
  prImpactEnabled: boolean;
};

export const projectQueries = {
  all: () => ['project'],
  default: () =>
    queryOptions({
      queryKey: [...projectQueries.all(), 'default'] as const,
      queryFn: async () => {
        const defaultProject = await getDefaultProject();

        return { defaultProject };
      },
    }),
  details: () => ['project', 'details'],
  detail: (projectId: string) =>
    queryOptions({
      queryKey: [...projectQueries.details(), projectId],
      queryFn: async () => {
        const project = await getProjectDetails(projectId);

        return project;
      },
    }),
} as const;

export const createProject = async (payload: {
  name: string;
}): Promise<void> => {
  const createResponse = await fetchBackend('/project/create', {
    method: 'POST',
    body: JSON.stringify(payload),
  });

  if (!createResponse.ok) {
    throw new Error(`Error: ${createResponse.statusText}`);
  }
};

export const getDefaultProject = async (): Promise<Project | undefined> => {
  const getResponse = await fetchBackend('/project/default', {
    method: 'GET',
  });

  if (!getResponse.ok) {
    throw new Error(`Error: ${getResponse.statusText}`);
  }

  const data = (await getResponse.json()) as { project: Project | null };

  return data.project ?? undefined;
};

export const getProjectDetails = async (
  projectId: string,
): Promise<ProjectDetails> => {
  const getResponse = await fetchBackend(`/project/${projectId}`, {
    method: 'GET',
  });

  if (!getResponse.ok) {
    throw new Error(`Error: ${getResponse.statusText}`);
  }

  const data = (await getResponse.json()) as { project: ProjectDetails };

  return data.project;
};

export const preLoadDefaultProject = async ({
  context: { queryClient },
}: {
  context: { queryClient: QueryClient };
}): Promise<Project> => {
  const { defaultProject } = await queryClient.ensureQueryData(
    projectQueries.default(),
  );

  if (defaultProject === undefined) {
    throw redirect({ to: '/new-project' });
  }

  return defaultProject;
};

export const deleteProject = async (projectId: string): Promise<void> => {
  const deleteResponse = await fetchBackend(`/project/${projectId}/admin`, {
    method: 'DELETE',
  });

  if (!deleteResponse.ok) {
    throw new Error(`Error: ${deleteResponse.statusText}`);
  }
};

export const leaveProject = async (projectId: string): Promise<void> => {
  const postResponse = await fetchBackend(`/project/${projectId}/leave`, {
    method: 'POST',
  });

  if (!postResponse.ok) {
    throw new Error(`Error: ${postResponse.statusText}`);
  }
};

export const inviteExistingUserToProject = async (
  projectId: string,
  payload: { email: string },
): Promise<ProjectMember> => {
  const postResponse = await fetchBackend(
    `/project/${projectId}/admin/invite`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
  );

  if (!postResponse.ok) {
    throw new Error(`Error: ${postResponse.statusText}`);
  }

  const data = (await postResponse.json()) as { member: ProjectMember };

  return data.member;
};

export const promoteProjectMember = async (
  projectId: string,
  userId: string,
): Promise<void> => {
  const postResponse = await fetchBackend(
    `/project/${projectId}/admin/promote-member`,
    {
      method: 'POST',
      body: JSON.stringify({ userId }),
    },
  );

  if (!postResponse.ok) {
    throw new Error(`Error: ${postResponse.statusText}`);
  }
};

export const updateProject = async (
  projectId: string,
  payload: {
    name?: string;
    prAiAnalysisEnabled?: boolean;
    prImpactEnabled?: boolean;
  },
): Promise<void> => {
  const putResponse = await fetchBackend(`/project/${projectId}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });

  if (!putResponse.ok) {
    throw new Error(`Error: ${putResponse.statusText}`);
  }

  return;
};
